/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext } from 'react';
import { compact } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { Text, BrandColors } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { convertToDateFormat } from 'blb-utils/date';
import { MeContext } from 'blb-contexts/Me';
import { getPersona, isClosingPersona, isLastSentAtPersona } from 'blb-persona/index';
import { LoanInfoRow } from './LoanInfoRow';
import { LenderWorkspaceAccessColumn } from './LenderWorkSpaceAccessColumn';

export const styles = StyleSheet.create({
  infoRow: {
    alignItems: 'center',
    display: 'flex',
  },
  largeMargin: {
    marginBottom: 10,
  },
  loanHeaderText: {
    textTransform: 'uppercase',
  },
  smallMargin: {
    marginBottom: 8,
  },
  summaryCard: {
    ':not(:last-child)': {
      marginRight: 48,
    },
  },
  summaryContainer: {
    display: 'flex',
    paddingBottom: 32,
  },
});

export namespace LoanInfoSummary {
  export interface Props {
    loan: BlendBridge.API.Loan;
  }
}

export const LoanInfoSummary: React.FC<LoanInfoSummary.Props> = ({ loan }) => {
  const {
    lender,
    loanRefNumber,
    loanType,
    primaryBorrowerName,
    propertyAddress,
    closing,
    lenderWorkspaceUrlData,
    loanAmount,
  } = loan;
  const propertyAddressDisplay = compact([
    propertyAddress?.lineText,
    propertyAddress?.additionalLineText,
    propertyAddress?.cityName,
    propertyAddress?.stateCode,
  ]).join(', ');

  const me = useContext(MeContext)!;
  const persona = getPersona(me.principalType);

  return (
    <div className={css(styles.summaryContainer)}>
      <div className={css(styles.summaryCard)}>
        <div className={css(styles.largeMargin, styles.loanHeaderText)}>
          <Text color={BrandColors.NIGHT_80} fontSize="sm" weight="semibold">
            {lender} #{loanRefNumber} | {loanType} | Loan Amount $
            {loanAmount ? loanAmount.toLocaleString('en-US') : '--'}
          </Text>
        </div>
        <div className={css(styles.smallMargin)}>
          <Text fontSize="lg" weight="medium">
            {primaryBorrowerName}
          </Text>
        </div>
        <div>
          <Text>{propertyAddressDisplay}</Text>
        </div>
      </div>
      {closing && isClosingPersona(persona) && (
        <div className={css(styles.summaryCard)}>
          <div className={css(styles.largeMargin, styles.loanHeaderText)}>
            <Text color={BrandColors.NIGHT_80} fontSize="sm" weight="semibold">
              Closing Info
            </Text>
          </div>
          <LoanInfoRow
            iconType="calendar_today"
            rowHeader="Closing Date"
            rowInfo={convertToDateFormat(
              closing.scheduledCeremonyTime ?? closing.closingStart!,
              'M/d/yyyy',
              closing.timezone,
            )}
          />
          <LoanInfoRow iconType="info" rowHeader="Closing Type" rowInfo={persona.getClosingTypeCopy(closing)} />
          {isLastSentAtPersona(persona) && persona.getLastSentAtRow(closing)}
        </div>
      )}
      {lenderWorkspaceUrlData && <LenderWorkspaceAccessColumn lenderWorkspaceUrlData={lenderWorkspaceUrlData} />}
    </div>
  );
};
